import { useState, useEffect, useCallback } from 'react';

/**
 * Global keyboard shortcuts registry — Wiz-parity `?` help sheet.
 *
 * Listens for `?` (Shift+/) on `window` and toggles a global modal that
 * displays the shared shortcut catalog. Skipped when focus is inside an
 * input/textarea/contenteditable so it never collides with typing.
 *
 * Page-local shortcuts (j/k/r/s/a/x/Space in IssuesPage) remain owned by
 * their pages — this hook only manages the global help affordance.
 *
 * The same modal can be opened from TopNav's "Help" button or anywhere in
 * the app via the `open-shortcuts` CustomEvent.
 */
export interface GlobalShortcut {
  key: string;
  description: string;
  scope?: 'global' | 'issues' | 'inventory';
}

export const GLOBAL_SHORTCUTS: GlobalShortcut[] = [
  { key: 'Ctrl+K', description: 'Open command palette', scope: 'global' },
  { key: '?', description: 'Toggle this shortcuts panel', scope: 'global' },
  { key: 'Esc', description: 'Close panels / clear selection', scope: 'global' },
  { key: '/', description: 'Focus search', scope: 'global' },
  { key: 'j', description: 'Next item in list', scope: 'issues' },
  { key: 'k', description: 'Previous item in list', scope: 'issues' },
  { key: 'Enter', description: 'Open selected item', scope: 'issues' },
  { key: 'Space', description: 'Toggle row selection', scope: 'issues' },
  { key: 'r', description: 'Refresh list', scope: 'issues' },
  { key: 's', description: 'Bulk snooze', scope: 'issues' },
  { key: 'a', description: 'Bulk assign', scope: 'issues' },
  { key: 'x', description: 'Clear selection', scope: 'issues' },
];

function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useGlobalShortcuts() {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen(prev => !prev), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // `?` is Shift+/; ignore when user is typing
      if (e.key === '?' && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(prev => !prev);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const eventOpener = () => setOpen(true);
    window.addEventListener('keydown', handler);
    window.addEventListener('open-shortcuts', eventOpener);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('open-shortcuts', eventOpener);
    };
  }, [open]);

  return { open, toggle, close };
}
